import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGql, formatDateTime, formatRelativeOrDate, Card, CardContent, Separator, Button } from '@ah/design';
import { Header, DetailSkeleton, PullToRefresh, SaveButton } from '@ah/design/mobile';
import { ExternalLink, BarChart3, Newspaper, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  NEWS_STORY_QUERY,
  RECORD_STORY_ENGAGEMENT_MUTATION,
  RECORD_STORY_SAVE_MUTATION,
  RECORD_STORY_UNSAVE_MUTATION,
  type NewsStory as Story,
  type StorySummary,
  type StoryArticle,
} from '../shared/index.ts';

export function NewsStory({ inSheet }: { inSheet?: boolean } = {}) {
  const gql = useGql();
  const qc = useQueryClient();
  const { id = '' } = useParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['news', 'story', id],
    queryFn: () => gql.request<{ newsStory: Story | null }>(NEWS_STORY_QUERY, { id }),
  });

  useEffect(() => {
    if (id) gql.request(RECORD_STORY_ENGAGEMENT_MUTATION, { id }).catch(() => {});
  }, [id, gql]);

  const body = isLoading ? (
    <DetailSkeleton hero="wide" />
  ) : error || !data?.newsStory ? (
    <div className="px-4 py-10 text-center">
      <p className="text-sm text-destructive mb-3">Story not found.</p>
      <button
        type="button"
        onClick={() => refetch()}
        className="text-sm px-3 py-1.5 rounded-full border border-border bg-card"
      >
        Retry
      </button>
    </div>
  ) : (
    <StoryBody story={data.newsStory} />
  );

  if (inSheet) return <>{body}</>;

  const trailing = data?.newsStory ? (
    <SaveButton
      isSaved={data.newsStory.isSaved}
      onSave={async () => {
        await gql.request(RECORD_STORY_SAVE_MUTATION, { id });
        qc.invalidateQueries({ queryKey: ['news', 'saved'] });
        qc.setQueryData(['news', 'story', id], (prev: { newsStory: Story | null } | undefined) =>
          prev?.newsStory ? { newsStory: { ...prev.newsStory, isSaved: true } } : prev,
        );
      }}
      onUnsave={async () => {
        await gql.request(RECORD_STORY_UNSAVE_MUTATION, { id });
        qc.invalidateQueries({ queryKey: ['news', 'saved'] });
        qc.setQueryData(['news', 'story', id], (prev: { newsStory: Story | null } | undefined) =>
          prev?.newsStory ? { newsStory: { ...prev.newsStory, isSaved: false } } : prev,
        );
      }}
    />
  ) : null;

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title={data?.newsStory?.title ?? 'News'} backTo="/news" trailing={trailing} />
      <PullToRefresh className="flex-1" onRefresh={() => refetch()}>
        {body}
      </PullToRefresh>
    </div>
  );
}

function StoryBody({ story }: { story: Story }) {
  const topics = story.summary?.relatedTopics?.length
    ? story.summary.relatedTopics
    : story.topics.map((t) => t.name);

  return (
    <article className="pb-10 space-y-5">
      {story.headlineImageUrl ? (
        <div className="aspect-[16/9] overflow-hidden bg-muted">
          <img src={story.headlineImageUrl} alt="" className="w-full h-full object-cover" />
        </div>
      ) : null}

      <div className="px-4 space-y-5">
      <header className="space-y-3">
        <h1 className="text-2xl font-bold leading-tight">{story.title}</h1>
        {topics.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {topics.slice(0, 6).map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wide rounded-full border px-2 py-0.5 text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
        <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Newspaper size={11} /> {story.articleCount}{' '}
            {story.articleCount === 1 ? 'source' : 'sources'}
          </span>
          {story.summaryGeneratedAt ? (
            <span className="inline-flex items-center gap-1">
              <Clock size={11} /> Updated {formatRelativeOrDate(story.summaryGeneratedAt)}
            </span>
          ) : null}
        </div>
      </header>

      {story.summary ? <SummaryBody summary={story.summary} /> : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            The summary for this story is still being generated. Check back in a moment.
          </CardContent>
        </Card>
      )}

      {story.sourceUrl ? (
        <a
          href={story.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary text-primary-foreground text-sm font-medium"
        >
          <ExternalLink size={14} /> Read original
        </a>
      ) : null}

      <ArticlesSection articles={story.articles} />
      </div>
    </article>
  );
}

function SummaryBody({ summary }: { summary: StorySummary }) {
  return (
    <div className="space-y-5">
      {summary.whatHappened ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">In Brief</h2>
          <p className="text-sm leading-relaxed">{summary.whatHappened}</p>
        </section>
      ) : null}

      {summary.longRead.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Full Story</h2>
          <div className="space-y-3 text-sm leading-relaxed">
            {summary.longRead.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>
      ) : null}

      {summary.whyItMatters ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Why it matters</h2>
          <p className="text-sm leading-relaxed">{summary.whyItMatters}</p>
        </section>
      ) : null}

      {summary.keyQuotes.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Key quotes</h2>
          <div className="space-y-3">
            {summary.keyQuotes.map((q, i) => (
              <blockquote key={i} className="border-l-2 border-muted pl-3">
                <p className="italic text-sm">"{q.quote}"</p>
                <p className="text-xs text-muted-foreground mt-1">
                  — {q.speaker}
                  {q.sourceName ? <span> · via {q.sourceName}</span> : null}
                </p>
              </blockquote>
            ))}
          </div>
        </section>
      ) : null}

      {summary.keyStats.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <BarChart3 size={16} className="text-muted-foreground" /> By the numbers
          </h2>
          <div className="grid gap-2">
            {summary.keyStats.map((s, i) => (
              <Card key={i} className="p-3">
                <p className="text-xl font-bold">{s.stat}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.context}</p>
                {s.sourceName ? (
                  <p className="text-[10px] text-muted-foreground mt-1.5">— {s.sourceName}</p>
                ) : null}
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      {summary.sourcesCited.length > 0 ? (
        <>
          <Separator />
          <section>
            <h2 className="text-lg font-semibold mb-2">Sources cited</h2>
            <ul className="space-y-2">
              {summary.sourcesCited.map((src, i) => (
                <li key={i} className="text-sm">
                  <a
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline inline-flex items-start gap-1"
                  >
                    <ExternalLink size={12} className="mt-0.5 shrink-0" />
                    <span>
                      {src.title}
                      {src.sourceName ? (
                        <span className="text-muted-foreground"> · {src.sourceName}</span>
                      ) : null}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        </>
      ) : null}
    </div>
  );
}

function ArticlesSection({ articles }: { articles: StoryArticle[] }) {
  const [showAll, setShowAll] = useState(false);
  if (articles.length === 0) return null;
  const visible = showAll ? articles : articles.slice(0, 5);
  return (
    <section>
      <h2 className="text-lg font-semibold mb-2">All coverage ({articles.length})</h2>
      <div className="space-y-2.5">
        {visible.map((a) => (
          <a
            key={a.id}
            href={a.articleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex gap-3"
          >
            {a.imageUrl ? (
              <img
                src={a.imageUrl}
                alt=""
                className="w-16 h-16 rounded object-cover shrink-0 bg-muted"
                loading="lazy"
              />
            ) : (
              <div className="w-16 h-16 rounded bg-muted shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium line-clamp-2">{a.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {a.sourceName ?? 'Unknown source'}
                {a.publishedAt ? <span> · {formatDateTime(a.publishedAt)}</span> : null}
              </p>
            </div>
          </a>
        ))}
      </div>
      {!showAll && articles.length > 5 ? (
        <Button
          variant="secondary"
          className="w-full mt-3"
          onClick={() => setShowAll(true)}
        >
          Show all ({articles.length})
        </Button>
      ) : null}
    </section>
  );
}
